import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SharedService } from '../../core_modules/common/services/shared.service';
import { BaseApiService } from '../../core_modules/common/services/base-api.service';
import { ApiUtils } from '../../core_modules/utils/api-utils/api-utils';
import { BEServerResponse } from '../../common_resource_modules/models/global/BEServerResponse';

@Injectable({
  providedIn: 'root',
})
export class AdminUserService extends BaseApiService<any> {
  static API_ADMIN_USER = 'admin-user';
  static API_ADMIN_PROFILE = 'admin-profile';
  static API_CITY = 'city';
  static API_BANK = 'bank';

  // Cache variables
  private activeProfilesCache: any[] | null = null;
  private activeCitiesCache: any[] | null = null;

  constructor(
    override readonly httpClient: HttpClient,
    override readonly sharedService: SharedService,
  ) {
    super(httpClient, sharedService);
  }

  protected getApi(): string {
    return AdminUserService.API_ADMIN_USER;
  }

  /** POST /v1/admin-user/search */
  searchUsers(params: {
    page: number;
    size: number;
    search: any[];
  }): Observable<any> {
    const req = ApiUtils.getRequest(
      `${AdminUserService.API_ADMIN_USER}/search`,
    );
    return this.httpClient.post<any>(req.url, params);
  }

  /** GET /v1/admin-user/{id} */
  getUserById(id: number): Observable<any> {
    const req = ApiUtils.getRequest(`${AdminUserService.API_ADMIN_USER}/${id}`);
    return this.httpClient.get<any>(req.url);
  }

  getUserByUsername(username: string): Observable<any> {
    const req = ApiUtils.getRequest(`${AdminUserService.API_ADMIN_USER}/check`);
    return this.httpClient.get<any>(req.url, { params: { username } });
  }

  /** POST /v1/admin-user/save */
  saveUser(body: any): Observable<any> {
    const req = ApiUtils.getRequest(`${AdminUserService.API_ADMIN_USER}/save`);
    return this.httpClient.post<any>(req.url, body);
  }

  /** POST /v1/admin-user/change-status */
  changeStatus(payload: { id: number; remarks: string }): Observable<any> {
    const req = ApiUtils.getRequest(
      `${AdminUserService.API_ADMIN_USER}/change-status`,
    );
    return this.httpClient.post<any>(req.url, payload);
  }

  logOutUser() {
    const api = `${this.getApi()}/log-out`;
    const req = ApiUtils.getRequest(api);
    return this.httpClient.get(req.url);
  }

  /** GET /v1/admin-profile/list */
  getActiveProfiles(): Observable<any> {
    if (this.activeProfilesCache) {
      return of({ data: this.activeProfilesCache });
    }
    const req = ApiUtils.getRequest(
      `${AdminUserService.API_ADMIN_PROFILE}/list`,
    );
    return this.httpClient.get<any>(req.url).pipe(
      tap((res) => {
        if (res?.data) {
          this.activeProfilesCache = res.data;
        }
      }),
    );
  }

  /** GET /v1/city/list */
  getActiveCities(): Observable<any> {
    if (this.activeCitiesCache) {
      return of({ data: this.activeCitiesCache });
    }
    const req = ApiUtils.getRequest(`${AdminUserService.API_CITY}/list`);
    return this.httpClient.get<any>(req.url).pipe(
      tap((res) => {
        if (res?.data) {
          this.activeCitiesCache = res.data;
        }
      }),
    );
  }

  /** GET /v1/bank/branch-by-bank */
  getActiveBanks(): Observable<BEServerResponse> {
    const req = ApiUtils.getRequest(`${AdminUserService.API_BANK}/all`);
    return this.httpClient.get<any>(req.url);
  }

  /** GET /v1/bank/branch-by-bank/:bankId */
  getActiveBankBranchByBankId(
    bankId: number | string,
  ): Observable<BEServerResponse> {
    const req = ApiUtils.getRequest(
      `${AdminUserService.API_BANK}/branch-by-bank/${bankId}`,
    );
    return this.httpClient.get<any>(req.url);
  }

  // Preload dependent dropdown data
  preloadDropdownData(): void {
    if (!this.activeProfilesCache) this.getActiveProfiles().subscribe();
    if (!this.activeCitiesCache) this.getActiveCities().subscribe();
  }

  resetPassword(data: {
    userId: number;
    newPassword: string;
  }): Observable<any> {
    const req = ApiUtils.getRequest(
      `${AdminUserService.API_ADMIN_USER}/reset-password`,
    );
    return this.httpClient.post<any>(req.url, data);
  }
}
